import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Button from "../../../../components/Button/Button";
import Loader from "../../../../components/Loader/Loader";
import "../Attendance.css";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Status strings are matched case-insensitively (see useStudentAttendance's
// normalizeStatus); anything not in this map simply renders without a dot.
const STATUS_TONE = {
  present: "success",
  absent: "danger",
  leave: "info",
  holiday: "warning",
};

/**
 * Presentational month grid. `days` is the normalized [{ date, status }]
 * list from useStudentAttendance; `month` is any Date inside the month to
 * display; `onMonthChange(nextMonthDate)` is called on prev/next.
 */
const AttendanceCalendar = ({ month, days = [], onMonthChange, isLoading = false }) => {
  const statusByDate = days.reduce((acc, day) => {
    if (day?.date) acc[day.date] = day.status;
    return acc;
  }, {});

  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const gridDays = eachDayOfInterval({ start: gridStart, end: gridEnd });

  return (
    <div className="attendance-calendar">
      <div className="attendance-calendar-header">
        <Button
          variant="link"
          size="sm"
          icon={<ChevronLeft size={18} />}
          onClick={() => onMonthChange(subMonths(month, 1))}
          aria-label="Previous month"
        />
        <div className="attendance-calendar-title">
          <div className="attendance-calendar-month">{format(month, "MMMM")}</div>
          <div className="attendance-calendar-year">{format(month, "yyyy")}</div>
        </div>
        <Button
          variant="link"
          size="sm"
          icon={<ChevronRight size={18} />}
          onClick={() => onMonthChange(addMonths(month, 1))}
          aria-label="Next month"
        />
      </div>

      <div className="attendance-calendar-weekdays">
        {WEEKDAYS.map((weekday) => (
          <div key={weekday} className="attendance-calendar-weekday">
            {weekday}
          </div>
        ))}
      </div>

      {isLoading ? (
        <div className="attendance-calendar-grid-loading">
          <Loader />
        </div>
      ) : (
        <div className="attendance-calendar-grid">
          {gridDays.map((day) => {
            const dateKey = format(day, "yyyy-MM-dd");
            const status = statusByDate[dateKey];
            const tone = status ? STATUS_TONE[status] : null;
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={dateKey}
                className={`attendance-calendar-cell${isSameMonth(day, month) ? "" : " is-outside"}`}
              >
                <span
                  className={`attendance-calendar-date${tone ? " has-status" : ""}${isToday ? " is-today" : ""}`}
                  data-tone={tone || undefined}
                >
                  {format(day, "d")}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AttendanceCalendar;
